import { parseDbTimestamp, pickT } from "@repo/shared";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { T } from "./order-flow-group.i18n";

type OrderFlowGroupProps = {
  flowName: string;
  completedAt: string | null;
  /** Right-hand summary, e.g. "6 orders". */
  summary: string;
  expanded: boolean;
  onToggle: () => void;
  language: string;
  children: ReactNode;
};

export function OrderFlowGroup({
  flowName,
  completedAt,
  summary,
  expanded,
  onToggle,
  language,
  children,
}: OrderFlowGroupProps) {
  const { formatDate } = useRegionalFormat();
  const t = pickT(T, language);
  return (
    <div className="border rounded-lg">
      {/* biome-ignore lint/a11y/useSemanticElements: div with role needed for click handling */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50",
          expanded && "border-b",
        )}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{flowName}</span>
            <span className="text-xs text-muted-foreground">
              {t.completed} {completedAt ? formatDate(parseDbTimestamp(completedAt)) : "—"}
            </span>
          </div>
        </div>
        <div className="text-right text-sm text-muted-foreground">{summary}</div>
      </div>
      {expanded && <div className="p-4 space-y-6">{children}</div>}
    </div>
  );
}
